import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { resendMfa, verifyMfa } from '../../actions/auth';
import { authFieldSx } from './fieldStyles';
import styles from './login.module.scss';

// API error codes after which this challenge is finished and the user has to
// enter their password again (the API's message already says so).
const RESTART_CODES = ['not_found', 'expired', 'too_many_attempts', 'too_many_sends', 'locked'];

const textSx = { color: '#fff', fontSize: '0.85rem', textAlign: 'center' };

// Wide enough for "Remember this browser for 30 days" on one line. In rem,
// not px: the app's root font size grows with the viewport width.
const COLUMN_WIDTH = '20rem';

// Second login step: enter the emailed code, optionally remember this browser.
const MfaCodeForm = ({ challenge, onVerified, onRestart }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector(state => state.auth);
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  // When the API will next accept a resend (it allows one every 60 seconds).
  // No countdown on screen: an early click just explains the wait.
  const [resendAvailableAt, setResendAvailableAt] = useState(
    () => Date.now() + (challenge.resendAfter || 0) * 1000
  );
  const [resendNote, setResendNote] = useState('');
  const [resending, setResending] = useState(false);
  const inputRef = useRef(null);

  const waitNote = seconds =>
    `You can request a new code in ${seconds} second${seconds === 1 ? '' : 's'}.`;

  const errorCode = err => err?.response?.data?.code;

  const onSubmit = async e => {
    e.preventDefault();
    if (code.length !== 6) return;
    try {
      await dispatch(verifyMfa({ challengeId: challenge.challengeId, code, rememberDevice }));
      onVerified();
    } catch (err) {
      if (RESTART_CODES.includes(errorCode(err))) {
        onRestart();
        return;
      }
      setCode('');
      inputRef.current?.focus();
    }
  };

  const onResend = async () => {
    const waitSeconds = Math.ceil((resendAvailableAt - Date.now()) / 1000);
    if (waitSeconds > 0) {
      setResendNote(waitNote(waitSeconds));
      return;
    }
    setResendNote('');
    setResending(true);
    try {
      const { resendAfter } = await dispatch(resendMfa(challenge.challengeId));
      setResendAvailableAt(Date.now() + resendAfter * 1000);
      setCode('');
      inputRef.current?.focus();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === 'resend_too_soon' && data.retry_after) {
        setResendAvailableAt(Date.now() + data.retry_after * 1000);
        setResendNote(waitNote(data.retry_after));
      } else if (RESTART_CODES.includes(data?.code)) {
        onRestart();
      }
    } finally {
      setResending(false);
    }
  };

  const minutes = Math.round((challenge.expiresIn || 600) / 60);

  return (
    <Box
      component='form'
      onSubmit={onSubmit}
      className={styles.login_form}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // login_form fixes the card at 350px while its padding grows with the
        // font, which squeezed this screen on wide monitors. Size to the
        // column instead, never wider than the screen.
        '&&': { width: 'auto', maxWidth: 'calc(100vw - 32px)' },
      }}
    >
      <Typography sx={{ ...textSx, width: COLUMN_WIDTH, maxWidth: '100%', mt: 2 }}>
        We've emailed you a 6-digit sign-in code. It expires in {minutes} minutes.
      </Typography>

      <Box component='div' sx={{ mt: 2, width: COLUMN_WIDTH, maxWidth: '100%' }}>
        <TextField
          inputRef={inputRef}
          autoFocus
          placeholder='Sign-in code'
          name='code'
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputProps={{
            inputMode: 'numeric',
            autoComplete: 'one-time-code',
            maxLength: 6,
            'aria-label': 'Sign-in code',
          }}
          sx={authFieldSx}
          fullWidth
          variant='outlined'
        />
      </Box>

      <FormControlLabel
        sx={{ width: COLUMN_WIDTH, maxWidth: '100%', mt: 1, mx: 0, '& .MuiFormControlLabel-label': textSx }}
        control={
          <Checkbox
            checked={rememberDevice}
            onChange={e => setRememberDevice(e.target.checked)}
            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
          />
        }
        label='Remember this browser for 30 days'
      />

      <Button
        className={`${styles['glowing-wrapper']} ${styles['glowing-wrapper-active']} ${styles.menu}`}
        sx={{ textDecoration: 'none', my: 2, width: `calc(${COLUMN_WIDTH} + 5px)`, maxWidth: '100%' }}
        type='submit'
        disabled={loading || code.length !== 6}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }} className={styles['navbar_button'] + ' ' + styles.resources}>
          {loading ? (
            <CircularProgress sx={{ color: '#fff' }} />
          ) : (
            <Typography className={styles['navbar_button-text']}>Verify</Typography>
          )}
        </Box>
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: COLUMN_WIDTH, maxWidth: '100%' }}>
        <Button
          variant='text'
          size='small'
          sx={{ color: '#fff', textTransform: 'none', px: 0 }}
          onClick={onRestart}
        >
          Back to login
        </Button>
        <Button
          variant='text'
          size='small'
          sx={{ color: '#fff', textTransform: 'none', px: 0 }}
          onClick={onResend}
          disabled={resending}
        >
          Resend code
        </Button>
      </Box>
      {resendNote && (
        <Typography role='status' sx={{ ...textSx, width: COLUMN_WIDTH, maxWidth: '100%', mt: 1, opacity: 0.8 }}>
          {resendNote}
        </Typography>
      )}
    </Box>
  );
};

export default MfaCodeForm;
